// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import { getIsAmazonAppStore, getIsAppleAppStore, isGooglePlatform } from '../helpers';
import { RootState } from '../reducers/reducers';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import Space from 'antd/lib/space';
import {
    AppPlatform,
    AppStoreAvailableBuild,
    AppStoreSelectedBuildReason,
    AppStoreType,
    BuildMatchingWarning,
    BuildMatchingWarningType,
    IntegrationType,
} from '../types';
import Tooltip from 'antd/lib/tooltip';
import { displayConsoleNameForAppStoreType, displayConsoleNameWithPrefix } from '../helpers/displayHelpers';
import { antGrey, runwayColors } from './styles/globalStyles';
import { displayNameForIntegrationType } from '../helpers/integrationHelpers';
import { InfoCircleOutlined, WarningFilled } from '@ant-design/icons';
import { Nullable, ReleaseComponentProps } from '../typescriptHelpers';
import { selectIsReleaseRollback } from '../selectors/selectors';
import AppStoreBuildComponent from './appStoreBuilds/AppStoreBuildComponent';
import AutomatedByRunwayIcon from './AutomatedByRunwayIcon';
import Flex from 'antd/lib/flex';
import Alert from 'antd/lib/alert';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';
import { buildRetainedBuildsExplanation } from './stepConfigs/RetainedBuildsSettingsField';

interface AppStoreBuildsProps extends ReleaseComponentProps {
    isSubmitted: boolean;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    additionalAppStoreSelectedBuilds: Nullable<string[]>;
    appStoreType: AppStoreType;
    platform: AppPlatform;
    buildMatchingWarnings?: BuildMatchingWarning[];
    willAutoPromotePreviousRelease?: boolean;
    disabledRollbackRequiredAutomationNames?: string[];
}

const AppStoreBuilds: React.FC<AppStoreBuildsProps> = (props: AppStoreBuildsProps) => {
    const selectedBuilds = props.appStoreSelectedBuilds;
    const additionalSelectedBuilds = props.additionalAppStoreSelectedBuilds;

    const isRollback = useSelector((state: RootState) => selectIsReleaseRollback(state, props.releaseId));
    const platform = props.platform;
    const appStoreType = props.appStoreType;
    const isAmazonAppStore = getIsAmazonAppStore(appStoreType);

    const allBuildSelectionWarnings = [];

    const buildsCanTakeAWhileText =
        'Builds can take up to five minutes to show up in Runway after being uploaded to App Store Connect.';

    const additionalBuildsComponent = (additionalSelectedBuildIds: string[]) => {
        return (
            <Space style={{ marginTop: 16 }} size={2}>
                <Text style={{ fontWeight: 600, marginRight: 6 }} type="secondary">
                    Retained builds
                </Text>
                <Tooltip title={buildRetainedBuildsExplanation({ showDocsLink: true })}>
                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                </Tooltip>
                <Text type="secondary">{`: ${additionalSelectedBuildIds.map((b) => `#${b}`).join(', ')}`}</Text>
            </Space>
        );
    };

    if (!selectedBuilds || selectedBuilds.length === 0) {
        const emptyText = emptyBuildText(
            appStoreType,
            isRollback,
            buildsCanTakeAWhileText,
            props.disabledRollbackRequiredAutomationNames,
        );
        return (
            <Space direction="vertical">
                <Text type="secondary">{emptyText}</Text>
                {additionalSelectedBuilds?.length > 0 && additionalBuildsComponent(additionalSelectedBuilds)}
            </Space>
        );
    }

    if (props.buildMatchingWarnings) {
        for (const warning of props.buildMatchingWarnings) {
            if (warning.type === BuildMatchingWarningType.NewerCIBuild) {
                allBuildSelectionWarnings.push(
                    <div>
                        <Text>{warning.message}</Text>
                        <Tooltip
                            title={
                                <>
                                    Runway was not able to definitively match your latest release candidate build to the
                                    selected build in the app store. Typically, this means the selected store build is
                                    not the latest build available. In some cases, this may be a false negative.{' '}
                                    <Link href={'https://docs.runway.team/using-runway/build-matching'} target="_blank">
                                        Read more
                                    </Link>
                                </>
                            }
                        >
                            <InfoCircleOutlined style={{ color: runwayColors.info }} />
                        </Tooltip>
                    </div>,
                );
            } else {
                allBuildSelectionWarnings.push(<Text>{warning.message}</Text>);
            }
        }
    }

    const appStoreBuilds = (selectedBuilds: AppStoreAvailableBuild[]) => {
        return (
            <Space>
                {selectedBuilds.map((selectedBuild) => {
                    return (
                        <AppStoreBuildComponent
                            {...selectedBuild}
                            key={selectedBuild.appStoreBuild.id}
                            styleOverrides={{
                                backgroundColor: antGrey.grey4,
                                border: `1px solid ${antGrey.grey5}`,
                            }}
                        />
                    );
                })}
            </Space>
        );
    };

    const selectedBuildReasonNode = (selectedBuilds: AppStoreAvailableBuild[]) => {
        if (selectedBuilds.length === 1) {
            const firstSelectedBuild = selectedBuilds.length > 0 ? selectedBuilds[0] : null;

            return displayTextForAppStoreSelectedBuildReason(
                firstSelectedBuild.appStoreBuild.buildNumber,
                firstSelectedBuild.selectedBuildReason,
                firstSelectedBuild.autoSelectedFromTrack,
                platform,
                appStoreType,
                props.isSubmitted,
                isRollback,
                firstSelectedBuild.associatedCIBuild?.integrationId,
            );
        } else if (selectedBuilds.length > 1) {
            return (
                <>
                    {selectedBuilds.map((selectedBuild) => {
                        return displayTextForAppStoreSelectedBuildReason(
                            selectedBuild.appStoreBuild.buildNumber,
                            selectedBuild.selectedBuildReason,
                            selectedBuild.autoSelectedFromTrack,
                            platform,
                            appStoreType,
                            props.isSubmitted,
                            isRollback,
                            selectedBuild.associatedCIBuild?.integrationId,
                        );
                    })}
                </>
            );
        }

        return null;
    };

    const showWarnings = !isAmazonAppStore && !props.isSubmitted;
    const hasWarnings = allBuildSelectionWarnings.length > 0;
    const hasMultipleWarnings = allBuildSelectionWarnings.length > 1;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {hasWarnings && showWarnings && (
                <Alert
                    type="warning"
                    message={
                        hasMultipleWarnings ?
                            <Flex vertical>
                                {allBuildSelectionWarnings.map((warning, index) => {
                                    return (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '14px auto 1fr',
                                                columnGap: 8,
                                                alignItems: 'start',
                                            }}
                                        >
                                            <span>
                                                {index === 0 && (
                                                    <WarningFilled style={{ color: runwayColors.statusYellow }} />
                                                )}
                                            </span>
                                            <Text>-</Text>
                                            {warning}
                                        </div>
                                    );
                                })}
                            </Flex>
                        :   <Space>
                                <WarningFilled style={{ color: runwayColors.statusYellow }} />
                                {allBuildSelectionWarnings[0]}
                            </Space>
                    }
                ></Alert>
            )}
            <Space direction="vertical" size={12}>
                {appStoreBuilds(selectedBuilds)}
                {selectedBuildReasonNode(selectedBuilds)}
            </Space>

            {props.willAutoPromotePreviousRelease && (
                <Space>
                    <Text type="secondary">Previous release will be auto-promoted to 100%</Text>
                    <Tooltip title="This option was selected on the submission step.">
                        <InfoCircleOutlined style={{ color: runwayColors.info }} />
                    </Tooltip>
                </Space>
            )}
            {additionalSelectedBuilds?.length > 0 && additionalBuildsComponent(additionalSelectedBuilds)}
        </div>
    );
};

export const emptyBuildText = (
    appStoreType: AppStoreType,
    isRollback: boolean,
    appleBuildDelayText: string,
    disabledRollbackRequiredAutomationNames: string[] = [],
) => {
    if (isRollback) {
        if (
            (appStoreType === AppStoreType.Apple || appStoreType === AppStoreType.Google) &&
            disabledRollbackRequiredAutomationNames.length > 0
        ) {
            const buildLabel = appStoreType === AppStoreType.Google ? 'APK/AAB' : 'build';
            const automationNames = disabledRollbackRequiredAutomationNames.map((name) => `“${name}”`).join(', ');
            return `No rollback ${buildLabel} selected yet. Enable the required automations to proceed with this rollback: ${automationNames}.`;
        }
        if (appStoreType === AppStoreType.Apple) {
            return "No rollback build selected yet. Runway will select it during submission preparation—you don't need to select it manually.";
        }
        if (appStoreType === AppStoreType.Google) {
            return "No rollback APK/AAB selected yet. Runway will select it during submission preparation—you don't need to select it manually.";
        }
    }

    return getIsAppleAppStore(appStoreType) ?
            `No selected build found. ${appleBuildDelayText}`
        :   'No selected build found.';
};

export default AppStoreBuilds;

const displayTextForAppStoreSelectedBuildReason = (
    buildNumber: string,
    selectedBuildReason: AppStoreSelectedBuildReason,
    autoSelectedFromTrack: string | null,
    platform: AppPlatform,
    appStoreType: AppStoreType,
    isSubmitted: boolean,
    isRollback: boolean,
    ciBuildIntegrationType?: IntegrationType,
) => {
    if (isRollback) {
        return null;
    }
    const sourceText =
        getIsAmazonAppStore(appStoreType) ?
            ciBuildIntegrationType ? displayNameForIntegrationType(ciBuildIntegrationType)
            :   'CI'
        :   displayConsoleNameForAppStoreType(appStoreType);

    switch (selectedBuildReason) {
        case AppStoreSelectedBuildReason.Rollback:
            return null;
        case AppStoreSelectedBuildReason.AutoSelection: {
            const endingText =
                isGooglePlatform(platform) && autoSelectedFromTrack ?
                    ` from the configured track '${autoSelectedFromTrack}'`
                :   '';
            return isSubmitted ?
                    <Space>
                        <AutomatedByRunwayIcon />
                        <Text type="secondary">
                            Runway automatically selected build (#{buildNumber}){endingText} for submission.
                        </Text>
                    </Space>
                :   <Space>
                        <AutomatedByRunwayIcon />
                        <Text type="secondary">
                            Runway automatically selected build (#{buildNumber}){endingText}.
                        </Text>
                        <TooltipInfoIcon tooltipContent="Any newer builds will be automatically selected, unless an older build is selected manually." />
                    </Space>;
        }
        case AppStoreSelectedBuildReason.ManualRunwaySelection:
            return isSubmitted ?
                    <Text type="secondary">
                        The submitted {displayConsoleNameForAppStoreType(appStoreType)} build (#{buildNumber}) was
                        manually selected in Runway.
                    </Text>
                :   <Space>
                        <Text type="secondary">
                            The selected build (#{buildNumber}) was manually changed in Runway.
                        </Text>
                        <TooltipInfoIcon
                            tooltipContent={`Runway will no longer automatically select the latest ${sourceText} build for this release.`}
                        />
                    </Space>;
        case AppStoreSelectedBuildReason.ManualStoreSelection:
            return isSubmitted ?
                    <Text type="secondary">
                        The submitted {displayConsoleNameForAppStoreType(appStoreType)} build (#{buildNumber}) was
                        manually selected in {displayConsoleNameWithPrefix(appStoreType)}.
                    </Text>
                :   <Space>
                        <Text type="secondary">
                            The selected {displayConsoleNameForAppStoreType(appStoreType)} build (#{buildNumber}) was
                            manually changed in {displayConsoleNameWithPrefix(appStoreType)}.
                        </Text>
                        <TooltipInfoIcon
                            tooltipContent={`Runway will no longer automatically select the latest ${sourceText} build for this release.`}
                        />
                    </Space>;
        case AppStoreSelectedBuildReason.ManualRCSelection:
            return isSubmitted ?
                    <Text type="secondary">
                        The submitted {displayConsoleNameForAppStoreType(appStoreType)} build&apos;s (#{buildNumber})
                        associated{' '}
                        {ciBuildIntegrationType ? displayNameForIntegrationType(ciBuildIntegrationType) : 'CI'} build
                        was manually selected in the Release Candidate step.
                    </Text>
                :   <Space>
                        <Text type="secondary">
                            The selected {displayConsoleNameForAppStoreType(appStoreType)} build&apos;s (#{buildNumber})
                            associated{' '}
                            {ciBuildIntegrationType ? displayNameForIntegrationType(ciBuildIntegrationType) : 'CI'}{' '}
                            build was manually selected in the Release Candidate step.
                        </Text>
                        <TooltipInfoIcon
                            tooltipContent={`Runway will no longer automatically select the latest ${sourceText} build for this release.`}
                        />
                    </Space>;
        default:
            return null;
    }
};
